import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Circle, X, Rocket, ArrowRight } from "lucide-react";

const KEY = "plumberhq_onboarding_dismissed_v1";

export default function OnboardingChecklist() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(KEY)) { setDismissed(true); return; }
    (async () => {
      try {
        const { data } = await api.get("/onboarding/progress");
        setData(data);
      } catch { /* ignore */ }
    })();
  }, []);

  if (dismissed || !data) return null;
  if (data.percent === 100) return null;

  const dismiss = () => { localStorage.setItem(KEY, "1"); setDismissed(true); };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 relative" data-testid="onboarding-checklist">
      <button onClick={dismiss} aria-label="Dismiss" className="absolute top-3 right-3 h-8 w-8 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 flex items-center justify-center" data-testid="onboarding-dismiss">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="h-11 w-11 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
          <Rocket className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>Get set up</h2>
          <p className="text-sm text-slate-500">Five quick steps to get your shop running.</p>
          <div className="mt-3 flex items-center gap-3">
            <div className="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all"
                style={{ width: `${data.percent}%` }}
                data-testid="onboarding-progress-bar"
              />
            </div>
            <div className="text-sm font-bold text-slate-900 tabular-nums" data-testid="onboarding-percent">
              {data.percent}%
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-2">
        {data.steps.map((s) => (
          <Link
            key={s.key}
            to={s.cta}
            data-testid={`onboarding-step-${s.key}`}
            className={`flex items-center gap-3 px-3 py-3 rounded-xl border transition ${
              s.done
                ? "bg-green-50 border-green-200 text-green-900"
                : "bg-white border-slate-200 hover:border-blue-300 hover:bg-blue-50"
            }`}
          >
            {s.done
              ? <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
              : <Circle className="w-5 h-5 text-slate-300 flex-shrink-0" />}
            <span className={`font-semibold text-sm flex-1 ${s.done ? "line-through opacity-70" : ""}`}>{s.label}</span>
            {!s.done && <ArrowRight className="w-4 h-4 text-slate-400" />}
          </Link>
        ))}
      </div>
    </div>
  );
}
